import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import UploadIcon from "../../Assets/img/upload.png";
import "../Services/Services.css";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import API from "../Service/Baseservice";
import Loading from "../../Loading/loading";
import Delete from "../../Assets/img/Delete.svg";
import OurGalleryPreview from "./OurGalleryPreview";

const ourGalleryServiceMap = {
    1: 2, // English
    2: 7, // French
};

const OurGalleryEdit = () => {
    const navigate = useNavigate();
    const { id: serviceIdParam } = useParams();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const lang = Number(queryParams.get("lang")) || 1;

    const [languageId, setLanguageId] = useState(lang);
    const [showPreviewOurGallery, setShowPreviewOurGallery] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [formData, setFormData] = useState({
        gallery_id: "",
        service_id: ourGalleryServiceMap[lang],
        language_id: lang,
        title: "",
        heading: "",
        description: "",
        active: 1,
        updatedBy: 1,
    });

    const [galleryImages, setGalleryImages] = useState([]);

    const [imageFiles, setImageFiles] = useState([]);

    const [loading, setLoading] = useState(true);

    const [popup, setPopup] = useState({
        show: false,
        title: "",
        message: "",
        type: "",
    });

    const fileInputRef = useRef();

    useEffect(() => {
        setLoading(true);

        API.post("ourgallery_list/condition", {
            service_id: ourGalleryServiceMap[languageId],
            language_id: languageId,
        })
            .then((res) => {
                const g = res?.data?.data?.data?.[0];
                if (!g) return;

                setFormData((prev) => ({
                    ...prev,
                    gallery_id: g.gallery_id,
                    service_id: g.service_id,
                    language_id: languageId,
                    title: g.title || "",
                    heading: g.heading || "",
                    description: g.description || "",
                }));

                setGalleryImages(g.images || []);

                const initialFiles = (g.images || []).map((it) => ({
                    image_id: it.image_id,
                    image: it.image,
                    preview: it.image,
                    file: null,
                    isNew: false,
                }));

                setImageFiles(initialFiles);
            })
            .catch((err) => {
                console.error("Failed to fetch gallery:", err);
            })
            .finally(() => setLoading(false));
    }, [languageId]);

    const handleImageUpload = (index, e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const updated = [...imageFiles];
            updated[index] = {
                ...updated[index],
                file: file,
                preview: reader.result,
                isNew: Boolean(!updated[index]?.image_id),
            };

            setImageFiles(updated);
        };

        reader.readAsDataURL(file);
    };

    const handleAddImage = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";

        input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onloadend = () => {
                setImageFiles((prev) => [
                    {
                        file: file,
                        preview: reader.result,
                        image: null,
                        image_id: null,
                        isNew: true,
                    },
                    ...prev,
                ]);

            };
            reader.readAsDataURL(file);
        };

        input.click();
    };

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });
    const ensureFullDataUri = (preview) => {
        if (!preview) return "";
        if (typeof preview === "string" && preview.startsWith("data:image")) return preview;
        return "";
    };

    const handleUpdate = () => {
        if (!formData.title.trim()) {
            return setPopup({
                show: true,
                title: "Required",
                message: "Title is required!",
                type: "info",
            });
        }

        if (!formData.heading.trim()) {
            return setPopup({
                show: true,
                title: "Required",
                message: "Heading is required!",
                type: "info",
            });
        }

        if (!formData.description.trim()) {
            return setPopup({
                show: true,
                title: "Required",
                message: "Description is required!",
                type: "info",
            });
        }

        if (imageFiles.length === 0) {
            return setPopup({
                show: true,
                title: "Required",
                message: "At least one image is required!",
                type: "info",
            });
        }

        setLoading(true);
        const uploadPromises = imageFiles.map((img) => {
            if (!img?.file) {
                return Promise.resolve(img.image || img.preview || "");
            }

            const fullDataUri = ensureFullDataUri(img.preview);
            if (!fullDataUri) {
                return Promise.resolve("");
            }

            return API.post("imageUpload/add", {
                name: img.file.name,
                image: [
                    {
                        name: img.file.name,
                        type: img.file.type,
                        size: `${img.file.size} bytes`,
                        base64: fullDataUri,
                    },
                ],
            })
                .then((res) => res?.data?.url || "")
                .catch((err) => {
                    console.error("imageUpload/add failed for", img.file.name, err);
                    return "";
                });
        });

        Promise.all(uploadPromises)
            .then((resolvedUrls) => {
                const updatedImagesPayload = [];
                const newImagesPayloads = [];

                resolvedUrls.forEach((url, i) => {
                    const img = imageFiles[i];
                    if (img.image_id) {
                        const finalUrl = url || img.image || "";
                        updatedImagesPayload.push({
                            image_id: img.image_id,
                            image: finalUrl,
                        });
                    } else {
                        const finalUrl = url || img.preview || img.image || "";
                        if (finalUrl) {
                            newImagesPayloads.push({
                                language_id: languageId,
                                gallery_id: formData.gallery_id,
                                image: finalUrl,
                                createdBy: 1,
                                updatedBy: 1,
                            });
                        }
                    }
                });
                const updatePayload = {
                    ...formData,
                    language_id: languageId,
                    galleryImages: updatedImagesPayload,
                };

                return API.post("ourgallery_edit/update", updatePayload)
                    .then((res) => {
                        if (newImagesPayloads.length === 0) {
                            return Promise.resolve(res);
                        }
                        const addCalls = newImagesPayloads.map((p) => API.post("add_ourgalleryimage/add", p));
                        return Promise.all(addCalls);
                    })
                    .then(() => {
                        setPopup({
                            show: true,
                            title: "Success",
                            message: "Gallery updated successfully!",
                            type: "success",
                        });

                        setTimeout(() => {
                            navigate(`/ourgalleryedit/${formData.service_id}?lang=${languageId}`);

                        }, 1500);
                    })
                    .catch((err) => {
                        console.error("Update/add sequence failed:", err);
                        setPopup({
                            show: true,
                            title: "Error",
                            message: "Failed to update gallery",
                            type: "error",
                        });
                    });
            })
            .catch((err) => {
                console.error("Promise.all(uploadPromises) failed:", err);
                setPopup({
                    show: true,
                    title: "Error",
                    message: "Image upload failed",
                    type: "error",
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };


    const handleDeleteImage = (index) => {
        const img = imageFiles[index];
        if (!img.image_id) {
            setImageFiles((prev) => prev.filter((_, i) => i !== index));
            return;
        }

        const payload = {
            image_id: img.image_id,
            active: 4,
        };

        API.post("ourgalleryimage_delete/update", payload)
            .then((res) => {
                console.log("Image deleted:", res);
                setImageFiles((prev) => prev.filter((_, i) => i !== index));
                setGalleryImages((prev) =>
                    prev.filter((item) => item.image_id !== img.image_id)
                );
            })
            .catch((err) => {
                console.error("Delete error:", err);
            });

    };

    const [deleteConfirm, setDeleteConfirm] = useState({
        show: false,
        index: null,
    });


    const handlePreview = () => {
        if (!formData.title.trim()) {
            return setPopup({
                show: true,
                title: "Required",
                message: "Title is required for preview!",
                type: "info",
            });
        }

        if (!formData.heading.trim()) {
            return setPopup({
                show: true,
                title: "Required",
                message: "Heading is required for preview!",
                type: "info",
            });
        }

        if (!formData.description.trim()) {
            return setPopup({
                show: true,
                title: "Required",
                message: "Description is required for preview!",
                type: "info",
            });
        }

        if (imageFiles.length === 0) {
            return setPopup({
                show: true,
                title: "Required",
                message: "At least one image is required for preview!",
                type: "info",
            });
        }

        // ✅ MATCH EXPECTED STRUCTURE
        setPreviewData({
            title: formData.title,
            heading: formData.heading,
            description: formData.description,
            videoTestimonials: imageFiles.map((img, index) => ({
                videotestimonial_id: index + 1,
                videos: img.preview || img.image
            }))
        });

        setShowPreviewOurGallery(true);
    };




    return (
        <div className="newss-containeradd">
            <div className="service-header-bar">
                <h2>Edit Our Gallery</h2>

                <div className="button-group">
                    <div className="filter-block">
                        <label>Language</label>
                        <div className="input-container">
                            <select
                                value={languageId}
                                onChange={(e) => {
                                    const newLang = Number(e.target.value);
                                    setLanguageId(newLang);
                                    navigate(`/ourgalleryedit/${ourGalleryServiceMap[newLang]}?lang=${newLang}`);
                                }}
                                className="input-field"
                            >
                                <option value={1}>English</option>
                                <option value={2}>Français</option>
                            </select>
                        </div>
                    </div>
                    <button onClick={handlePreview} className="language-save-btn">
                        Preview
                    </button>
                    <button className="language-save-btn" onClick={handleUpdate}>
                        Save
                    </button>

                    <button className="language-cancel-btn" onClick={() => navigate("/services")}>
                        Back
                    </button>
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : (
                <>
                    <div className="service-container">
                        {/* LEFT FORM */}
                        <div className="ourgallery-left-form">
                            <div className="form-group-services">
                                <label>
                                    Title <span className="required">*</span>
                                </label>
                                <input name="title" value={formData.title} onChange={handleChange} />
                            </div>

                            <div className="form-group-services">
                                <label>
                                    Heading <span className="required">*</span>
                                </label>
                                <input name="heading" value={formData.heading} onChange={handleChange} />
                            </div>

                            <div className="news-form-group editor-big">
                                <label>
                                    Description <span className="required">*</span>
                                </label>

                                <CKEditor
                                    editor={ClassicEditor}
                                    data={formData.description}
                                    onChange={(e, editor) => setFormData({ ...formData, description: editor.getData() })}
                                />
                            </div>
                        </div>

                        {/* RIGHT IMAGES */}
                        <div className="ourgallery-right-form">
                            <div className="image-add">
                                <label>
                                    Images <span className="required">*</span>
                                </label>

                                <button className="add-btn" onClick={handleAddImage}>
                                    + Add Image
                                </button>

                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    onChange={() => { }}
                                />
                            </div>

                            <div className="ourgallery-right-section">
                                {imageFiles.map((img, i) => (
                                    <div className="ourgallery-upload-section">
                                        <div className="ourgallery-upload-box">
                                            {img?.preview || img?.image ? (
                                                <img
                                                    src={img.preview || img.image}
                                                    className="preview-img"
                                                    alt={`gallery-${i}`}
                                                />
                                            ) : (
                                                <>
                                                    <img src={UploadIcon} className="upload-icon" alt="upload icon" />
                                                    <p>Click to upload</p>
                                                </>
                                            )}


                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleImageUpload(i, e)}
                                            />


                                            <img
                                                src={Delete}
                                                alt="Delete"
                                                className="delete-img-btn"
                                                onClick={() => setDeleteConfirm({ show: true, index: i })}

                                            />
                                        </div>
                                    </div>

                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {popup.show && (
                <div className="popup-overlay">
                    <div className={`popup-box popup-${popup.type}`}>
                        <h3>{popup.title}</h3>
                        <p>{popup.message}</p>
                        <button className="btn ok-btn" onClick={() => setPopup({ ...popup, show: false })}>
                            OK
                        </button>
                    </div>
                </div>


            )}

            {deleteConfirm.show && (
                <div className="popup-overlay">
                    <div className="popup-box popup-warning">
                        <h3>Delete Image</h3>
                        <p>Are you sure you want to delete this image?</p>

                        <div className="popup-buttons">
                            <button
                                className="btn ok-btn"
                                onClick={() => {
                                    handleDeleteImage(deleteConfirm.index);
                                    setDeleteConfirm({ show: false, index: null });
                                }}
                            >
                                Delete
                            </button>

                            <button
                                className="cancel-btn "
                                onClick={() => setDeleteConfirm({ show: false, index: null })}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showPreviewOurGallery && (
                <OurGalleryPreview
                    previewService={previewData}
                    setShowPreviewOurGallery={setShowPreviewOurGallery}
                />
            )}
        </div>
    );
};

export default OurGalleryEdit;

